import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { CITIES } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Download, TrendingUp, Users, BedDouble, Building } from 'lucide-react';

export default function AdminIzvjestaji() {
  const { user } = useAuth();
  const [city, setCity] = useState(user?.role === 'sluzbenik' ? user.city : 'all');
  const [period, setPeriod] = useState('2025');
  const [monthly, setMonthly] = useState([]);
  const [citiesReport, setCitiesReport] = useState([]);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    const load = async () => {
      const results = await Promise.allSettled([
        api.get('/reports/monthly'), api.get('/reports/cities'), api.get('/reports/countries')
      ]);
      const val = (r) => (r.status === 'fulfilled' ? r.value.data : []);
      setMonthly(val(results[0]));
      setCitiesReport(val(results[1]));
      setCountries(val(results[2]));
    };
    load();
  }, []);

  const cityData = useMemo(
    () => (city === 'all' ? citiesReport : citiesReport.filter((c) => c.city === city)),
    [city, citiesReport]
  );

  const totals = useMemo(() => ({
    prijave: cityData.reduce((s, c) => s + c.prijave, 0),
    nocenja: cityData.reduce((s, c) => s + c.nocenja, 0),
    objekata: cityData.reduce((s, c) => s + c.objekata, 0)
  }), [cityData]);

  const maxPrijave = Math.max(1, ...monthly.map((m) => m.prijave));
  const maxGosti = Math.max(1, ...countries.map((c) => c.gosti));

  return (
    <div data-testid="admin-izvjestaji">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0b1a3d]">Izvještaji – prijave i odjave turista</h1>
          <p className="text-sm text-slate-500">Integracija sa sistemom za prijavu i odjavu turista na nivou kantona (demo podaci).</p>
        </div>
        <Button onClick={() => toast.success('Izvještaj je generisan i preuzet (demo).')} data-testid="export-report-btn"
          className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200">
          <Download className="h-4 w-4 mr-2" /> Izvezi izvještaj
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 p-4 mb-6 flex flex-wrap gap-3">
        <Select value={city} onValueChange={setCity} disabled={user?.role === 'sluzbenik'}>
          <SelectTrigger className="border-slate-200 w-56" data-testid="report-city-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Svi gradovi/općine</SelectItem>
            {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="border-slate-200 w-40" data-testid="report-period-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="2025">2025. godina</SelectItem>
            <SelectItem value="2024">2024. godina</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Ukupno prijava turista', value: totals.prijave.toLocaleString('bs'), icon: Users },
          { label: 'Ukupno noćenja', value: totals.nocenja.toLocaleString('bs'), icon: BedDouble },
          { label: 'Registrovanih objekata', value: totals.objekata, icon: Building }
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-100 p-5 flex items-center gap-4" data-testid={`report-stat-${i}`}>
            <div className="w-11 h-11 rounded-lg bg-[#e8ecf7] flex items-center justify-center">
              <s.icon className="h-5 w-5 text-[#001752]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#001752]">{s.value}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* MJESEČNI GRAF */}
        <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="monthly-chart">
          <h2 className="font-bold text-[#0b1a3d] mb-5 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[#001752]" /> Prijave turista po mjesecima ({period}.)
          </h2>
          <div className="space-y-3">
            {monthly.map((m) => (
              <div key={m.month} className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-16 shrink-0">{m.month}</span>
                <div className="flex-1 bg-[#f0f3fb] rounded-full h-6 overflow-hidden">
                  <div className="h-full bg-[#001752] rounded-full flex items-center justify-end px-2 transition-all duration-500"
                    style={{ width: `${(m.prijave / maxPrijave) * 100}%` }}>
                    <span className="text-[10px] text-white font-semibold">{m.prijave.toLocaleString('bs')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ZEMLJE */}
        <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="countries-chart">
          <h2 className="font-bold text-[#0b1a3d] mb-5">Gosti po zemljama porijekla</h2>
          <div className="space-y-3">
            {countries.map((c) => (
              <div key={c.country} className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-32 shrink-0 truncate">{c.country}</span>
                <div className="flex-1 bg-[#f0f3fb] rounded-full h-6 overflow-hidden">
                  <div className="h-full bg-[#4a6bb5] rounded-full flex items-center justify-end px-2 transition-all duration-500"
                    style={{ width: `${(c.gosti / maxGosti) * 100}%` }}>
                    <span className="text-[10px] text-white font-semibold">{c.gosti.toLocaleString('bs')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* TABELA PO GRADOVIMA */}
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden lg:col-span-2" data-testid="city-report-table">
          <div className="p-6 pb-3"><h2 className="font-bold text-[#0b1a3d]">Pregled po gradovima/općinama</h2></div>
          <Table>
            <TableHeader>
              <TableRow className="bg-[#f8f9fd]">
                <TableHead className="text-[#001752] font-semibold">Grad/Općina</TableHead>
                <TableHead className="text-[#001752] font-semibold text-right">Prijave</TableHead>
                <TableHead className="text-[#001752] font-semibold text-right">Noćenja</TableHead>
                <TableHead className="text-[#001752] font-semibold text-right">Objekata</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cityData.map((c) => (
                <TableRow key={c.city}>
                  <TableCell className="font-medium text-slate-700">{c.city}</TableCell>
                  <TableCell className="text-right text-slate-600">{c.prijave.toLocaleString('bs')}</TableCell>
                  <TableCell className="text-right text-slate-600">{c.nocenja.toLocaleString('bs')}</TableCell>
                  <TableCell className="text-right text-slate-600">{c.objekata}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
